import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Play, Trash2, Search, RefreshCw, MapPin, Calendar, ChevronUp, ChevronDown, Filter, X, List, Map } from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import ModuleHeader from "@/components/ModuleHeader";

const ESTADOS = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
  "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"
];

const ESTADOS_NOME: Record<string, string> = {
  AC:"Acre",AL:"Alagoas",AM:"Amazonas",AP:"Amapá",BA:"Bahia",CE:"Ceará",
  DF:"Distrito Federal",ES:"Espírito Santo",GO:"Goiás",MA:"Maranhão",
  MG:"Minas Gerais",MS:"Mato Grosso do Sul",MT:"Mato Grosso",PA:"Pará",
  PB:"Paraíba",PE:"Pernambuco",PI:"Piauí",PR:"Paraná",RJ:"Rio de Janeiro",
  RN:"Rio Grande do Norte",RO:"Rondônia",RR:"Roraima",RS:"Rio Grande do Sul",
  SC:"Santa Catarina",SE:"Sergipe",SP:"São Paulo",TO:"Tocantins"
};

const STATUS_LABELS: Record<string, string> = { AT: "Ativo", PE: "Pendente", SU: "Suspenso/Cancelado" };
const TIPO_LABELS: Record<string, string> = { IRU: "Imóvel Rural", AST: "Assentamento", PCT: "Território Tradicional" };

function ImportTab({ isImporting }: { isImporting?: boolean }) {
  const [selectedUfs, setSelectedUfs] = useState<Set<string>>(new Set());
  const [municipio, setMunicipio] = useState("");
  const { toast } = useToast();
  const qc = useQueryClient();

  const toggleUf = (uf: string) => {
    setSelectedUfs((prev) => {
      const next = new Set(prev);
      if (next.has(uf)) next.delete(uf);
      else next.add(uf);
      return next;
    });
  };

  const selectAll = () => setSelectedUfs(new Set(ESTADOS));
  const clearAll = () => setSelectedUfs(new Set());

  const importMutation = useMutation({
    mutationFn: () =>
      authFetch("/api/sicar/import", {
        method: "POST",
        body: JSON.stringify({
          ufs: Array.from(selectedUfs),
          municipio: municipio || undefined,
        }),
      }),
    onSuccess: (data) => {
      toast({ title: "Importação iniciada", description: data.message });
      qc.invalidateQueries({ queryKey: ["/api/sicar/import-progress"] });
    },
    onError: (err: Error) => {
      const msg = err.message.includes("401") ? "API Key não configurada ou inválida" : err.message;
      toast({ title: "Erro", description: msg, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Selecionar Estados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={selectAll} data-testid="button-select-all">
              Selecionar Todos
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll} data-testid="button-clear-all">
              Limpar
            </Button>
          </div>
          <div className="grid grid-cols-9 gap-2" data-testid="grid-estados">
            {ESTADOS.map((uf) => (
              <Button
                key={uf}
                variant={selectedUfs.has(uf) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleUf(uf)}
                data-testid={`button-uf-${uf}`}
                className="text-xs font-mono"
              >
                {uf}
              </Button>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Label className="whitespace-nowrap">Filtrar município:</Label>
            <Input
              value={municipio}
              onChange={(e) => setMunicipio(e.target.value)}
              placeholder="Opcional - nome do município"
              className="max-w-sm"
              data-testid="input-municipio-filter"
            />
          </div>

          <div className="mt-4">
            <Button
              onClick={() => importMutation.mutate()}
              disabled={selectedUfs.size === 0 || importMutation.isPending || isImporting}
              data-testid="button-start-import"
            >
              {importMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {isImporting ? "Importação em andamento..." : `Iniciar Importação (${selectedUfs.size} UF${selectedUfs.size !== 1 ? "s" : ""})`}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CacheTab({ isImporting }: { isImporting?: boolean }) {
  const [searchCode, setSearchCode] = useState("");
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searchError, setSearchError] = useState("");
  const { toast } = useToast();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["/api/sicar/cache-stats"],
    queryFn: () => authFetch("/api/sicar/cache-stats"),
    refetchInterval: isImporting ? 10000 : false,
    enabled: true,
  });

  const searchByCode = async () => {
    if (!searchCode.trim()) return;
    setSearchError("");
    setSearchResult(null);
    try {
      const result = await authFetch(`/api/sicar/${encodeURIComponent(searchCode.trim())}`);
      setSearchResult(result);
    } catch (err: any) {
      setSearchError(err.message);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Busca por Código CAR
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              placeholder="Ex: MG-3106200-abc123..."
              className="max-w-lg"
              data-testid="input-search-code"
              onKeyDown={(e) => e.key === "Enter" && searchByCode()}
            />
            <Button onClick={searchByCode} data-testid="button-search-code">
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
          </div>
          {searchError && <p className="text-destructive text-sm mt-2" data-testid="text-search-error">{searchError}</p>}
          {searchResult && (
            <div className="mt-4 p-4 bg-muted rounded-lg text-sm" data-testid="card-search-result">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                <p><strong>Código:</strong> {searchResult.cod_imovel}</p>
                <p><strong>UF:</strong> {searchResult.uf}</p>
                <p><strong>Município:</strong> {searchResult.municipio}</p>
                <p><strong>Cód. IBGE:</strong> {searchResult.cod_municipio_ibge || "N/A"}</p>
                <p><strong>Área (ha):</strong> {searchResult.num_area?.toFixed(2) || "N/A"}</p>
                <p><strong>Módulos Fiscais:</strong> {searchResult.m_fiscal?.toFixed(4) || "N/A"}</p>
                <p><strong>Status:</strong> {searchResult.ind_status || "N/A"}</p>
                <p><strong>Tipo:</strong> {searchResult.ind_tipo || "N/A"}</p>
                <p><strong>Condição:</strong> {searchResult.condicao || "N/A"}</p>
                <p><strong>Criação CAR:</strong> {searchResult.dat_criacao ? new Date(searchResult.dat_criacao).toLocaleDateString("pt-BR") : "N/A"}</p>
                <p><strong>Última Atualização:</strong> {searchResult.data_atualizacao ? new Date(searchResult.data_atualizacao).toLocaleDateString("pt-BR") : "N/A"}</p>
                <p><strong>Importado em:</strong> {searchResult.fetched_at ? new Date(searchResult.fetched_at).toLocaleString("pt-BR") : "N/A"}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="grid-cache-stats">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-primary" data-testid="text-total-cache">
              {stats?.total?.toLocaleString("pt-BR") || 0}
            </div>
            <div className="text-sm text-muted-foreground">Total de imóveis no cache</div>
          </CardContent>
        </Card>

        {stats?.porUf?.map((item: any) => (
          <Card key={item.uf} data-testid={`card-cache-uf-${item.uf}`}>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-xl font-bold">{item.uf}</div>
                  <div className="text-sm text-muted-foreground">{ESTADOS_NOME[item.uf] || item.uf}</div>
                </div>
                <Badge variant="secondary">{parseInt(item.total).toLocaleString("pt-BR")}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Atualizado: {item.ultima_atualizacao ? new Date(item.ultima_atualizacao).toLocaleString("pt-BR") : "N/A"}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

const DIAS_SEMANA = [
  { value: "0", label: "Domingo" },
  { value: "1", label: "Segunda" },
  { value: "2", label: "Terça" },
  { value: "3", label: "Quarta" },
  { value: "4", label: "Quinta" },
  { value: "5", label: "Sexta" },
  { value: "6", label: "Sábado" },
];

function cronToReadable(cron: string): string {
  const parts = cron.split(" ");
  if (parts.length < 5) return cron;
  const [min, hour, dayMonth, , dayWeek] = parts;
  const timeStr = `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;

  if (dayMonth === "*" && dayWeek === "*") return `Diário às ${timeStr}`;
  if (dayMonth === "*" && dayWeek !== "*") {
    const dias = dayWeek.split(",").map((d) => DIAS_SEMANA.find((ds) => ds.value === d)?.label || d);
    return `Semanal - ${dias.join(", ")} às ${timeStr}`;
  }
  if (dayMonth !== "*" && dayWeek === "*") {
    const dias = dayMonth.split(",");
    if (dias.length === 2) return `Quinzenal - dias ${dias.join(" e ")} às ${timeStr}`;
    return `Mensal - dia ${dayMonth} às ${timeStr}`;
  }
  return cron;
}

function ScheduleTab() {
  const [selectedUfs, setSelectedUfs] = useState<Set<string>>(new Set());
  const [frequencia, setFrequencia] = useState("");
  const [diaSemana, setDiaSemana] = useState("0");
  const [diaMes, setDiaMes] = useState("1");
  const [diaQuinzenal1, setDiaQuinzenal1] = useState("1");
  const [diaQuinzenal2, setDiaQuinzenal2] = useState("15");
  const [hora, setHora] = useState("03");
  const [minuto, setMinuto] = useState("00");
  const [customCron, setCustomCron] = useState("");
  const [descricao, setDescricao] = useState("");
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: schedules, isLoading } = useQuery({
    queryKey: ["/api/sicar/schedules"],
    queryFn: () => authFetch("/api/sicar/schedules"),
    enabled: true,
  });

  const usedUfs = new Set<string>(
    (schedules || []).filter((s: any) => s.ativo).map((s: any) => s.uf)
  );

  const toggleUf = (uf: string) => {
    if (usedUfs.has(uf)) return;
    setSelectedUfs((prev) => {
      const next = new Set(prev);
      if (next.has(uf)) next.delete(uf);
      else next.add(uf);
      return next;
    });
  };

  const buildCron = (): string => {
    const h = parseInt(hora) || 0;
    const m = parseInt(minuto) || 0;
    switch (frequencia) {
      case "diario": return `${m} ${h} * * *`;
      case "semanal": return `${m} ${h} * * ${diaSemana}`;
      case "quinzenal": return `${m} ${h} ${diaQuinzenal1},${diaQuinzenal2} * *`;
      case "mensal": return `${m} ${h} ${diaMes} * *`;
      case "custom": return customCron;
      default: return "";
    }
  };

  const buildDescricao = (): string => {
    if (descricao) return descricao;
    const cronExpr = buildCron();
    return cronToReadable(cronExpr);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const cronExpr = buildCron();
      const desc = buildDescricao();
      const ufs = Array.from(selectedUfs);
      const successes: string[] = [];
      const failures: string[] = [];
      for (const u of ufs) {
        try {
          await authFetch("/api/sicar/schedule", {
            method: "POST",
            body: JSON.stringify({ uf: u, cronExpression: cronExpr, descricao: desc }),
          });
          successes.push(u);
        } catch {
          failures.push(u);
        }
      }
      return { successes, failures };
    },
    onSuccess: ({ successes, failures }) => {
      qc.invalidateQueries({ queryKey: ["/api/sicar/schedules"] });
      if (successes.length > 0) {
        toast({ title: "Rotina(s) criada(s)", description: `${successes.length} criada(s): ${successes.join(", ")}` });
        setSelectedUfs(new Set());
        setFrequencia("");
        setDescricao("");
        setCustomCron("");
      }
      if (failures.length > 0) {
        toast({ title: "Falhas", description: `Erro ao criar para: ${failures.join(", ")}`, variant: "destructive" });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, ativo }: { id: number; ativo: boolean }) =>
      authFetch(`/api/sicar/schedule/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ ativo }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/sicar/schedules"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      authFetch(`/api/sicar/schedule/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast({ title: "Rotina removida" });
      qc.invalidateQueries({ queryKey: ["/api/sicar/schedules"] });
    },
  });

  const canCreate = selectedUfs.size > 0 && frequencia && (frequencia !== "custom" || customCron);
  const cronPreview = buildCron();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Nova Rotina de Importação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="mb-2 block">Estados (UFs já agendadas ficam desabilitadas)</Label>
            <div className="grid grid-cols-9 gap-2" data-testid="grid-schedule-estados">
              {ESTADOS.map((uf) => {
                const isUsed = usedUfs.has(uf);
                const isSelected = selectedUfs.has(uf);
                return (
                  <Button
                    key={uf}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleUf(uf)}
                    disabled={isUsed}
                    className={`text-xs font-mono ${isUsed ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid={`button-sched-uf-${uf}`}
                    title={isUsed ? `${uf} já possui rotina ativa` : ESTADOS_NOME[uf]}
                  >
                    {uf}
                  </Button>
                );
              })}
            </div>
            {selectedUfs.size > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Selecionados: {Array.from(selectedUfs).join(", ")}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Frequência</Label>
              <Select value={frequencia} onValueChange={setFrequencia}>
                <SelectTrigger data-testid="select-frequencia">
                  <SelectValue placeholder="Selecione a frequência..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="diario">Diário</SelectItem>
                  <SelectItem value="semanal">Semanal</SelectItem>
                  <SelectItem value="quinzenal">Quinzenal</SelectItem>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="custom">Personalizado (Cron)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {frequencia === "semanal" && (
              <div>
                <Label>Dia da Semana</Label>
                <Select value={diaSemana} onValueChange={setDiaSemana}>
                  <SelectTrigger data-testid="select-dia-semana">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIAS_SEMANA.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {frequencia === "quinzenal" && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label>Primeiro dia</Label>
                  <Select value={diaQuinzenal1} onValueChange={setDiaQuinzenal1}>
                    <SelectTrigger data-testid="select-quinzenal-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <SelectItem key={d} value={String(d)}>Dia {d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Segundo dia</Label>
                  <Select value={diaQuinzenal2} onValueChange={setDiaQuinzenal2}>
                    <SelectTrigger data-testid="select-quinzenal-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <SelectItem key={d} value={String(d)}>Dia {d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {frequencia === "mensal" && (
              <div>
                <Label>Dia do Mês</Label>
                <Select value={diaMes} onValueChange={setDiaMes}>
                  <SelectTrigger data-testid="select-dia-mes">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <SelectItem key={d} value={String(d)}>Dia {d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {frequencia === "custom" && (
            <div>
              <Label>Expressão Cron</Label>
              <Input
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                placeholder="Ex: 0 3 * * 1 (segunda às 3h)"
                data-testid="input-custom-cron"
              />
            </div>
          )}

          {frequencia && frequencia !== "custom" && (
            <div className="grid grid-cols-2 gap-3 max-w-xs">
              <div>
                <Label>Hora</Label>
                <Select value={hora} onValueChange={setHora}>
                  <SelectTrigger data-testid="select-hora">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                      <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}h</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Minuto</Label>
                <Select value={minuto} onValueChange={setMinuto}>
                  <SelectTrigger data-testid="select-minuto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 15, 30, 45].map((m) => (
                      <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {cronPreview && frequencia && (
            <div className="p-3 bg-muted/50 rounded-lg text-sm" data-testid="cron-preview">
              <span className="text-muted-foreground">Expressão cron: </span>
              <code className="font-mono bg-muted px-2 py-0.5 rounded">{cronPreview}</code>
              <span className="text-muted-foreground ml-2">({cronToReadable(cronPreview)})</span>
            </div>
          )}

          <div>
            <Label>Descrição (opcional)</Label>
            <Input
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Auto-gerada se vazia"
              data-testid="input-schedule-desc"
            />
          </div>

          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canCreate || createMutation.isPending}
            data-testid="button-create-schedule"
          >
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Criar Rotina ({selectedUfs.size} UF{selectedUfs.size !== 1 ? "s" : ""})
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rotinas Cadastradas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !schedules?.length ? (
            <p className="text-muted-foreground text-sm" data-testid="text-no-schedules">Nenhuma rotina cadastrada</p>
          ) : (
            <div className="space-y-3">
              {schedules.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`row-schedule-${s.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge>{s.uf}</Badge>
                      <span className="text-sm font-medium">{cronToReadable(s.cron_expression)}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <code className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">{s.cron_expression}</code>
                      {s.descricao && <span className="text-xs text-muted-foreground">{s.descricao}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {s.ultima_execucao
                        ? `Última: ${new Date(s.ultima_execucao).toLocaleString("pt-BR")}`
                        : "Nunca executada"}
                      {s.proxima_execucao && ` | Próxima: ${new Date(s.proxima_execucao).toLocaleString("pt-BR")}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={s.ativo}
                      onCheckedChange={(checked) =>
                        toggleMutation.mutate({ id: s.id, ativo: checked })
                      }
                      data-testid={`switch-schedule-${s.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMutation.mutate(s.id)}
                      data-testid={`button-delete-schedule-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function HistoryTab() {
  const [page, setPage] = useState(0);
  const [filterUf, setFilterUf] = useState("");
  const limit = 20;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["/api/sicar/import-history", `?limit=${limit}&offset=${page * limit}${filterUf ? `&uf=${filterUf}` : ""}`],
    queryFn: () =>
      authFetch(`/api/sicar/import-history?limit=${limit}&offset=${page * limit}${filterUf ? `&uf=${filterUf}` : ""}`),
    enabled: true,
  });

  const statusColor = (s: string) => {
    if (s === "completed") return "default" as const;
    if (s === "running") return "secondary" as const;
    return "destructive" as const;
  };

  const statusLabel = (s: string) => {
    if (s === "completed") return "Concluído";
    if (s === "running") return "Em execução";
    return "Erro";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={filterUf || "all"} onValueChange={(v) => { setFilterUf(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-48" data-testid="select-history-uf">
            <SelectValue placeholder="Filtrar por UF" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as UFs</SelectItem>
            {ESTADOS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh-history">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : !data?.data?.length ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center" data-testid="text-no-history">Nenhum registro de importação encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3">UF</th>
                  <th className="text-left p-3">Município</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Imóveis</th>
                  <th className="text-right p-3">Erros</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Início</th>
                  <th className="text-right p-3">Duração</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row: any) => (
                  <tr key={row.id} className="border-t" data-testid={`row-history-${row.id}`}>
                    <td className="p-3 font-mono font-bold">{row.uf}</td>
                    <td className="p-3">{row.municipio || "Todos"}</td>
                    <td className="p-3">
                      <Badge variant={statusColor(row.status)}>{statusLabel(row.status)}</Badge>
                    </td>
                    <td className="p-3 text-right">{(row.total_imoveis || 0).toLocaleString("pt-BR")}</td>
                    <td className="p-3 text-right">{row.total_erros || 0}</td>
                    <td className="p-3">
                      <Badge variant="outline">{row.tipo}</Badge>
                    </td>
                    <td className="p-3 text-xs">
                      {row.iniciado_em ? new Date(row.iniciado_em).toLocaleString("pt-BR") : "N/A"}
                    </td>
                    <td className="p-3 text-right text-xs">
                      {row.duracao_segundos != null ? `${row.duracao_segundos}s` : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {data.total} registro(s)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                data-testid="button-history-prev"
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={(page + 1) * limit >= data.total}
                onClick={() => setPage((p) => p + 1)}
                data-testid="button-history-next"
              >
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ImoveisTab() {
  const [filters, setFilters] = useState({
    uf: "", municipio: "", cod_imovel: "", ind_status: "", ind_tipo: "",
    condicao: "", area_min: "", area_max: "", m_fiscal_min: "", m_fiscal_max: "",
    dat_criacao_de: "", dat_criacao_ate: "",
    data_atualizacao_de: "", data_atualizacao_ate: "",
    fetched_at_de: "", fetched_at_ate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sort, setSort] = useState("cod_imovel");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [detailRow, setDetailRow] = useState<any>(null);
  const limit = 50;

  const { data: filterOptions } = useQuery({
    queryKey: ["/api/sicar/imoveis/filter-options"],
    queryFn: () => authFetch("/api/sicar/imoveis/filter-options"),
    enabled: true,
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (appliedFilters.uf) p.set("uf", appliedFilters.uf);
    if (appliedFilters.municipio) p.set("municipio", appliedFilters.municipio);
    if (appliedFilters.cod_imovel) p.set("cod_imovel", appliedFilters.cod_imovel);
    if (appliedFilters.ind_status) p.set("ind_status", appliedFilters.ind_status);
    if (appliedFilters.ind_tipo) p.set("ind_tipo", appliedFilters.ind_tipo);
    if (appliedFilters.condicao) p.set("condicao", appliedFilters.condicao);
    if (appliedFilters.area_min) p.set("area_min", appliedFilters.area_min);
    if (appliedFilters.area_max) p.set("area_max", appliedFilters.area_max);
    if (appliedFilters.m_fiscal_min) p.set("m_fiscal_min", appliedFilters.m_fiscal_min);
    if (appliedFilters.m_fiscal_max) p.set("m_fiscal_max", appliedFilters.m_fiscal_max);
    if (appliedFilters.dat_criacao_de) p.set("dat_criacao_de", appliedFilters.dat_criacao_de);
    if (appliedFilters.dat_criacao_ate) p.set("dat_criacao_ate", appliedFilters.dat_criacao_ate);
    if (appliedFilters.data_atualizacao_de) p.set("data_atualizacao_de", appliedFilters.data_atualizacao_de);
    if (appliedFilters.data_atualizacao_ate) p.set("data_atualizacao_ate", appliedFilters.data_atualizacao_ate);
    if (appliedFilters.fetched_at_de) p.set("fetched_at_de", appliedFilters.fetched_at_de);
    if (appliedFilters.fetched_at_ate) p.set("fetched_at_ate", appliedFilters.fetched_at_ate);
    p.set("sort", sort);
    p.set("order", order);
    p.set("limit", String(limit));
    p.set("offset", String(page * limit));
    return p.toString();
  }, [appliedFilters, sort, order, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/sicar/imoveis", queryString],
    queryFn: () => authFetch(`/api/sicar/imoveis?${queryString}`),
    enabled: true,
  });

  const applyFilters = () => { setAppliedFilters({ ...filters }); setPage(0); };
  const clearFilters = () => {
    const empty = { uf: "", municipio: "", cod_imovel: "", ind_status: "", ind_tipo: "", condicao: "", area_min: "", area_max: "", m_fiscal_min: "", m_fiscal_max: "", dat_criacao_de: "", dat_criacao_ate: "", data_atualizacao_de: "", data_atualizacao_ate: "", fetched_at_de: "", fetched_at_ate: "" };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(0);
  };

  const toggleSort = (col: string) => {
    if (sort === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(col); setOrder("asc"); }
    setPage(0);
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sort !== col) return null;
    return order === "asc" ? <ChevronUp className="h-3 w-3 inline ml-1" /> : <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  const totalPages = data?.total ? Math.ceil(data.total / limit) : 0;
  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="button-toggle-filters">
          <Filter className="h-4 w-4 mr-2" />
          Filtros {hasActiveFilters && <Badge variant="secondary" className="ml-2">{Object.values(appliedFilters).filter(Boolean).length}</Badge>}
        </Button>
        {data && <span className="text-sm text-muted-foreground">{data.total?.toLocaleString("pt-BR")} imóvel(is) encontrado(s)</span>}
      </div>

      {showFilters && (
        <Card data-testid="card-filters">
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">UF</Label>
                <Select value={filters.uf || "all"} onValueChange={(v) => setFilters({ ...filters, uf: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-uf"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {(filterOptions?.ufs || ESTADOS).map((u: string) => <SelectItem key={u} value={u}>{u} - {ESTADOS_NOME[u] || u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Município</Label>
                <Input value={filters.municipio} onChange={(e) => setFilters({ ...filters, municipio: e.target.value })} placeholder="Buscar..." data-testid="filter-municipio" />
              </div>
              <div>
                <Label className="text-xs">Código CAR</Label>
                <Input value={filters.cod_imovel} onChange={(e) => setFilters({ ...filters, cod_imovel: e.target.value })} placeholder="Buscar..." data-testid="filter-cod-imovel" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filters.ind_status || "all"} onValueChange={(v) => setFilters({ ...filters, ind_status: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {(filterOptions?.status || []).map((s: string) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s} ({s})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={filters.ind_tipo || "all"} onValueChange={(v) => setFilters({ ...filters, ind_tipo: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {(filterOptions?.tipos || []).map((t: string) => <SelectItem key={t} value={t}>{TIPO_LABELS[t] || t} ({t})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Condição</Label>
                <Select value={filters.condicao || "all"} onValueChange={(v) => setFilters({ ...filters, condicao: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-condicao"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {(filterOptions?.condicoes || []).map((c: string) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Área mín (ha)</Label>
                  <Input type="number" value={filters.area_min} onChange={(e) => setFilters({ ...filters, area_min: e.target.value })} placeholder="0" data-testid="filter-area-min" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Área máx (ha)</Label>
                  <Input type="number" value={filters.area_max} onChange={(e) => setFilters({ ...filters, area_max: e.target.value })} placeholder="∞" data-testid="filter-area-max" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">M.Fiscal mín</Label>
                  <Input type="number" value={filters.m_fiscal_min} onChange={(e) => setFilters({ ...filters, m_fiscal_min: e.target.value })} placeholder="0" data-testid="filter-mfiscal-min" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">M.Fiscal máx</Label>
                  <Input type="number" value={filters.m_fiscal_max} onChange={(e) => setFilters({ ...filters, m_fiscal_max: e.target.value })} placeholder="∞" data-testid="filter-mfiscal-max" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Criação CAR de</Label>
                  <Input type="date" value={filters.dat_criacao_de} onChange={(e) => setFilters({ ...filters, dat_criacao_de: e.target.value })} data-testid="filter-data-de" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Criação CAR até</Label>
                  <Input type="date" value={filters.dat_criacao_ate} onChange={(e) => setFilters({ ...filters, dat_criacao_ate: e.target.value })} data-testid="filter-data-ate" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Atualização de</Label>
                  <Input type="date" value={filters.data_atualizacao_de} onChange={(e) => setFilters({ ...filters, data_atualizacao_de: e.target.value })} data-testid="filter-atualizacao-de" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Atualização até</Label>
                  <Input type="date" value={filters.data_atualizacao_ate} onChange={(e) => setFilters({ ...filters, data_atualizacao_ate: e.target.value })} data-testid="filter-atualizacao-ate" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Importado de</Label>
                  <Input type="date" value={filters.fetched_at_de} onChange={(e) => setFilters({ ...filters, fetched_at_de: e.target.value })} data-testid="filter-fetched-de" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Importado até</Label>
                  <Input type="date" value={filters.fetched_at_ate} onChange={(e) => setFilters({ ...filters, fetched_at_ate: e.target.value })} data-testid="filter-fetched-ate" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={applyFilters} size="sm" data-testid="button-apply-filters">
                <Search className="h-4 w-4 mr-2" />Filtrar
              </Button>
              <Button onClick={clearFilters} variant="outline" size="sm" data-testid="button-clear-filters">
                <X className="h-4 w-4 mr-2" />Limpar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : !data?.data?.length ? (
        <Card><CardContent className="pt-6"><p className="text-muted-foreground text-center" data-testid="text-no-imoveis">Nenhum imóvel encontrado</p></CardContent></Card>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-imoveis">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("cod_imovel")} data-testid="th-cod-imovel">
                    Código <SortIcon col="cod_imovel" />
                  </th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => toggleSort("uf")} data-testid="th-uf">
                    UF <SortIcon col="uf" />
                  </th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => toggleSort("municipio")} data-testid="th-municipio">
                    Município <SortIcon col="municipio" />
                  </th>
                  <th className="text-right p-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("num_area")} data-testid="th-area">
                    Área (ha) <SortIcon col="num_area" />
                  </th>
                  <th className="text-right p-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("m_fiscal")} data-testid="th-mfiscal">
                    M.Fiscal <SortIcon col="m_fiscal" />
                  </th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => toggleSort("ind_status")} data-testid="th-status">
                    Status <SortIcon col="ind_status" />
                  </th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("condicao")} data-testid="th-condicao">
                    Condição <SortIcon col="condicao" />
                  </th>
                  <th className="text-left p-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("dat_criacao")} data-testid="th-dat-criacao">
                    Criação <SortIcon col="dat_criacao" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row: any) => (
                  <tr
                    key={row.id}
                    className="border-t hover:bg-muted/50 cursor-pointer"
                    onClick={() => setDetailRow(detailRow?.id === row.id ? null : row)}
                    data-testid={`row-imovel-${row.id}`}
                  >
                    <td className="p-3 font-mono text-xs max-w-[200px] truncate" title={row.cod_imovel}>{row.cod_imovel}</td>
                    <td className="p-3 font-bold">{row.uf}</td>
                    <td className="p-3">{row.municipio}</td>
                    <td className="p-3 text-right">{row.num_area?.toFixed(2)}</td>
                    <td className="p-3 text-right">{row.m_fiscal?.toFixed(2)}</td>
                    <td className="p-3"><Badge variant="outline">{STATUS_LABELS[row.ind_status] || row.ind_status}</Badge></td>
                    <td className="p-3 text-xs">{TIPO_LABELS[row.ind_tipo] || row.ind_tipo}</td>
                    <td className="p-3 text-xs max-w-[180px] truncate" title={row.condicao}>{row.condicao}</td>
                    <td className="p-3 text-xs whitespace-nowrap">{row.dat_criacao ? new Date(row.dat_criacao).toLocaleDateString("pt-BR") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Dialog open={!!detailRow} onOpenChange={(open) => { if (!open) setDetailRow(null); }}>
            <DialogContent className="max-w-lg" data-testid="dialog-detail-row">
              <DialogHeader>
                <DialogTitle className="font-mono text-sm break-all">{detailRow?.cod_imovel}</DialogTitle>
              </DialogHeader>
              {detailRow && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground text-xs">UF</span>
                    <p className="font-semibold">{detailRow.uf}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Município</span>
                    <p className="font-semibold">{detailRow.municipio}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Cód. IBGE</span>
                    <p className="font-semibold">{detailRow.cod_municipio_ibge || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Área (ha)</span>
                    <p className="font-semibold">{detailRow.num_area?.toFixed(4)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Módulos Fiscais</span>
                    <p className="font-semibold">{detailRow.m_fiscal?.toFixed(4)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Status</span>
                    <p><Badge variant="outline">{STATUS_LABELS[detailRow.ind_status] || detailRow.ind_status}</Badge></p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Tipo</span>
                    <p className="font-semibold">{TIPO_LABELS[detailRow.ind_tipo] || detailRow.ind_tipo}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Condição</span>
                    <p className="font-semibold">{detailRow.condicao || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Criação CAR</span>
                    <p className="font-semibold">{detailRow.dat_criacao ? new Date(detailRow.dat_criacao).toLocaleDateString("pt-BR") : "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">Última Atualização</span>
                    <p className="font-semibold">{detailRow.data_atualizacao ? new Date(detailRow.data_atualizacao).toLocaleDateString("pt-BR") : "N/A"}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground text-xs">Importado em</span>
                    <p className="font-semibold">{detailRow.fetched_at ? new Date(detailRow.fetched_at).toLocaleString("pt-BR") : "N/A"}</p>
                  </div>

                  {(detailRow.alt_media !== null && detailRow.alt_media !== undefined) && (
                    <>
                      <div className="sm:col-span-2 pt-2 border-t border-border">
                        <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Dados de Altitude & Relevo</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Altitude Média</span>
                        <p className="font-semibold">{Number(detailRow.alt_media) === -9999 ? "N/A" : `${detailRow.alt_media} m`}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Alt. Mín / Máx</span>
                        <p className="font-semibold">
                          {Number(detailRow.alt_min) === -9999 ? "N/A" : `${detailRow.alt_min} m`}
                          {" / "}
                          {Number(detailRow.alt_max) === -9999 ? "N/A" : `${detailRow.alt_max} m`}
                        </p>
                      </div>
                    </>
                  )}

                  {(detailRow.argila_pct !== null && detailRow.argila_pct !== undefined) && (
                    <>
                      <div className="sm:col-span-2 pt-2 border-t border-border">
                        <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Dados de Solo</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Argila (%)</span>
                        <p className="font-semibold">{detailRow.argila_pct}%</p>
                      </div>
                      {detailRow.argila_0_30 && (
                        <div>
                          <span className="text-muted-foreground text-xs">Argila por Profundidade</span>
                          <p className="font-semibold text-xs">
                            0-30cm: {detailRow.argila_0_30}% | 30-60cm: {detailRow.argila_30_60}% | 60-100cm: {detailRow.argila_60_100}%
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              Página {page + 1} de {totalPages} ({data.total?.toLocaleString("pt-BR")} registros)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="button-imoveis-prev">Anterior</Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="button-imoveis-next">Próxima</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatElapsed(startIso: string | null): string {
  if (!startIso) return "";
  const diff = Math.floor((Date.now() - new Date(startIso).getTime()) / 1000);
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

function ImportProgressBanner({ progress }: { progress: any }) {
  const isRunning = progress?.running;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [isRunning]);

  if (!isRunning) return null;

  const pct = progress.municipiosTotal
    ? Math.round((progress.municipiosProcessados / progress.municipiosTotal) * 100)
    : 0;

  const elapsed = formatElapsed(progress.iniciadoEm);
  const speed = progress.iniciadoEm && progress.imoveisImportados > 0
    ? Math.round(progress.imoveisImportados / ((Date.now() - new Date(progress.iniciadoEm).getTime()) / 60000))
    : 0;

  return (
    <Card className="border-primary/50 bg-primary/5 animate-pulse-subtle" data-testid="banner-import-progress">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="font-medium text-sm">Importação em andamento</span>
          {elapsed && <Badge variant="outline" className="text-xs">{elapsed}</Badge>}
        </div>

        <div className="flex justify-between text-sm">
          <span>
            UF: <strong>{progress.currentUf}</strong>
            {progress.currentMunicipio && (
              <> — <strong>{progress.currentMunicipio}</strong></>
            )}
          </span>
          <span className="text-muted-foreground">{pct}%</span>
        </div>

        <Progress value={pct} className="h-2" data-testid="progress-bar-global" />

        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>{progress.municipiosProcessados}/{progress.municipiosTotal} municípios</span>
          <span>{progress.imoveisImportados?.toLocaleString("pt-BR")} imóveis importados</span>
          {progress.municipioAtualImoveis > 0 && (
            <span>Município atual: {progress.municipioAtualImoveis} imóveis</span>
          )}
          {speed > 0 && <span>{speed} imóveis/min</span>}
          {progress.erros > 0 && <span className="text-destructive">{progress.erros} erro(s)</span>}
        </div>

        {progress.fila?.length > 0 && (
          <div className="text-xs text-muted-foreground">
            Na fila: {progress.fila.join(", ")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SicarPage() {
  const { data: progress } = useQuery({
    queryKey: ["/api/sicar/import-progress"],
    queryFn: () => authFetch("/api/sicar/import-progress"),
    refetchInterval: 3000,
  });

  return (
    <div className="space-y-6" data-testid="page-sicar">
      <ModuleHeader
        icon={Map}
        iconColor="text-green-600"
        title="MVCAR — Dados Geográficos"
        description="Importação e consulta de imóveis rurais do Sistema Nacional de Cadastro Ambiental Rural"
        docsHref="/docs"
      />

      <ImportProgressBanner progress={progress} />

      <Tabs defaultValue="imoveis" data-testid="tabs-mvcar">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="imoveis" data-testid="tab-imoveis">
            <List className="h-4 w-4 mr-1" />Imóveis
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">Importar</TabsTrigger>
          <TabsTrigger value="cache" data-testid="tab-cache">Cache</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="tab-schedules">Rotinas</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Histórico</TabsTrigger>
        </TabsList>
        <TabsContent value="imoveis"><ImoveisTab /></TabsContent>
        <TabsContent value="import"><ImportTab isImporting={progress?.running} /></TabsContent>
        <TabsContent value="cache"><CacheTab isImporting={progress?.running} /></TabsContent>
        <TabsContent value="schedules"><ScheduleTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}
